import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  CircularProgress,
  InputAdornment
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { FMRApiService } from '../services/api';

interface SearchResult {
  type: 'zipcode' | 'city';
  zipCode: string;
  city: string;
  county: string;
  state: string;
  stateAbbr: string;
  label: string;
}

const SearchAutocomplete: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();

  // Debounced search function
  const searchAutocomplete = useCallback(
    async (searchQuery: string) => {
      if (searchQuery.length < 2) {
        setResults([]);
        setShowResults(false);
        return;
      }

      setLoading(true);
      try {
        const response = await FMRApiService.autocomplete(searchQuery, 10);
        if (response.success) {
          setResults(response.results);
          setShowResults(true);
        }
      } catch (error) {
        console.error('Autocomplete error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // Debounce the search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchAutocomplete(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, searchAutocomplete]);

  const handleResultClick = (result: SearchResult) => {
    if (/^\d{5}$/.test(query) || result.type === 'zipcode') {
      // Navigate to ZIP code page
      navigate(`/zipcode/${result.zipCode}`);
    } else {
      // Navigate to city page
      navigate(`/city/${encodeURIComponent(result.city)}/${result.stateAbbr}`);
    }
    setQuery('');
    setShowResults(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    const trimmed = query.trim();
    // 5-digit ZIP shortcut: navigate directly even if autocomplete index missed it
    if (/^\d{5}$/.test(trimmed)) {
      navigate(`/zipcode/${trimmed}`);
      setQuery('');
      setShowResults(false);
      return;
    }
    // Otherwise pick the first autocomplete result
    if (results.length > 0) {
      handleResultClick(results[0]);
    }
  };

  const handleBlur = () => {
    // Delay hiding results to allow click to register
    setTimeout(() => {
      setShowResults(false);
    }, 200);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        placeholder="Search by ZIP code or city name..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => query.length >= 2 && setShowResults(true)}
        onBlur={handleBlur}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: loading ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white',
          },
        }}
      />

      {showResults && results.length > 0 && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 9999, // Very high z-index to ensure dropdown appears above all content
            maxHeight: '400px',
            overflow: 'auto',
            mt: 0.5,
          }}
          elevation={8} // Higher elevation for better shadow
        >
          <List disablePadding>
            {results.map((result, index) => (
              <ListItem key={index} disablePadding>
                <ListItemButton onClick={() => handleResultClick(result)}>
                  <ListItemText
                    primary={result.label}
                    secondary={
                      <Typography variant="body2" color="text.secondary">
                        {result.county} County, {result.state}
                      </Typography>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      {showResults && query.length >= 2 && results.length === 0 && !loading && (
        <Paper
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 9999, // Very high z-index to ensure dropdown appears above all content
            mt: 0.5,
            p: 2,
          }}
          elevation={8} // Higher elevation for better shadow
        >
          <Typography variant="body2" color="text.secondary" align="center">
            No results found for "{query}"
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default SearchAutocomplete;
